import { Point } from '../../math';
const dirBySide = {
    Left: { x: -1, y: 0 },
    Right: { x: 1, y: 0 },
    Top: { x: 0, y: -1 },
    Bottom: { x: 0, y: 1 }
};
function axisFromSide(side) {
    return side === 'Left' || side === 'Right' ? 'H' : 'V';
}
function add(p, d, k) {
    return new Point(p.x + d.x * k, p.y + d.y * k);
}
function samePoint(a, b) {
    return a.x === b.x && a.y === b.y;
}
function segmentOrientation(a, b) {
    return a.y === b.y ? 'H' : 'V';
}
function applyLock(last, lock) {
    return lock.axis === 'x' ? new Point(lock.value, last.y) : new Point(last.x, lock.value);
}
function legacyNoLockBetweenAnchors(start, end, sourceSide, targetSide, sameSideDistance, source, target) {
    // Handle null sides for orphan connections
    if (!sourceSide || !targetSide) {
        // Simple direct path for connections without connectors
        return [];
    }
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const sourceVert = sourceSide === 'Top' || sourceSide === 'Bottom';
    const targetVert = targetSide === 'Top' || targetSide === 'Bottom';
    const ssd = sameSideDistance !== null && sameSideDistance !== void 0 ? sameSideDistance : 30;
    if (sourceVert) {
        if (targetVert) {
            if (sourceSide === targetSide) {
                const srcY = source ? source.y : start.y;
                const tgtY = target ? target.y : end.y;
                const pointY = sourceSide === 'Top' ? Math.min(srcY, tgtY) - ssd : Math.max(srcY, tgtY) + ssd;
                return [new Point(start.x, pointY), new Point(end.x, pointY)];
            }
            const midY = start.y + deltaY / 2;
            return [new Point(start.x, midY), new Point(end.x, midY)];
        }
        return [new Point(start.x, end.y)];
    }
    if (!targetVert) {
        if (sourceSide === targetSide) {
            const srcX = source ? source.x : start.x;
            const tgtX = target ? target.x : end.x;
            const pointX = sourceSide === 'Left' ? Math.min(srcX, tgtX) - ssd : Math.max(srcX, tgtX) + ssd;
            return [new Point(pointX, start.y), new Point(pointX, end.y)];
        }
        const midX = start.x + deltaX / 2;
        return [new Point(midX, start.y), new Point(midX, end.y)];
    }
    return [new Point(end.x, start.y)];
}
function chooseElbowL(start, end, preferFirst, preferLast) {
    const optA = { mid: new Point(end.x, start.y), first: 'H', last: 'V' };
    const optB = { mid: new Point(start.x, end.y), first: 'V', last: 'H' };
    let candidates = [optA, optB];
    if (preferFirst) {
        const byFirst = candidates.filter((o) => o.first === preferFirst);
        if (byFirst.length)
            candidates = byFirst;
    }
    if (preferLast && candidates.length > 1) {
        const byLast = candidates.filter((o) => o.last === preferLast);
        if (byLast.length)
            candidates = byLast;
    }
    return candidates[0].mid;
}
function expandVirtualPointsToRoute(vps, locks, sourceSide, targetSide) {
    const fullPoints = [vps[0].p];
    const segmentsMeta = [];
    const preferFirstFor = (vp) => {
        if (vp.kind === 'connectionStart')
            return axisFromSide(sourceSide);
        if (vp.kind === 'lock' && vp.createdByLockIndex != null) {
            return locks[vp.createdByLockIndex].axis === 'y' ? 'H' : 'V';
        }
        return null;
    };
    const preferLastFor = (vp) => {
        if (vp.kind === 'connectionEnd')
            return axisFromSide(targetSide);
        return null;
    };
    for (let i = 0; i < vps.length - 1; i++) {
        const startVP = vps[i];
        const endVP = vps[i + 1];
        const start = startVP.p;
        const end = endVP.p;
        const startLockIndex = startVP.createdByLockIndex;
        const startLock = startLockIndex != null ? locks[startLockIndex] : null;
        const nextLockIndex = endVP.createdByLockIndex != null ? endVP.createdByLockIndex : locks.length;
        const pushSegment = (a, b, isFirstOutOfStart) => {
            if (samePoint(a, b))
                return;
            const orient = segmentOrientation(a, b);
            let owner = null;
            if (isFirstOutOfStart &&
                startLockIndex != null &&
                startLock &&
                ((startLock.axis === 'y' && orient === 'H') || (startLock.axis === 'x' && orient === 'V'))) {
                owner = startLockIndex;
            }
            segmentsMeta.push({
                a,
                b,
                orientation: orient,
                ownerLockIndex: owner,
                insertIndex: nextLockIndex
            });
        };
        const last = fullPoints[fullPoints.length - 1];
        const a = samePoint(last, start) ? start : last;
        if (a.x === end.x || a.y === end.y) {
            pushSegment(a, end, true);
            fullPoints.push(end);
        }
        else {
            const mid = chooseElbowL(a, end, preferFirstFor(startVP), preferLastFor(endVP));
            pushSegment(a, mid, true);
            fullPoints.push(mid);
            pushSegment(mid, end, false);
            fullPoints.push(end);
        }
    }
    const dedup = [];
    for (const p of fullPoints) {
        if (dedup.length === 0 || !samePoint(dedup[dedup.length - 1], p))
            dedup.push(p);
    }
    return { points: dedup.slice(1, -1), segmentsMeta };
}
export function buildLegacyConnection(opts) {
    const { source, target, sourceSide, targetSide, anchorOffset = 10, sameSideDistance } = opts;
    const connectionStart = sourceSide ? add(source, dirBySide[sourceSide], anchorOffset) : source;
    const connectionEnd = targetSide ? add(target, dirBySide[targetSide], anchorOffset) : target;
    const mids = legacyNoLockBetweenAnchors(connectionStart, connectionEnd, sourceSide, targetSide, sameSideDistance, source, target);
    // For legacy routing, only include source, mids, and target (no connectionStart/connectionEnd)
    const vps = [
        { p: source, kind: 'source', createdByLockIndex: null }
    ];
    for (const m of mids)
        vps.push({ p: m, kind: 'lock', createdByLockIndex: null });
    if (!samePoint(vps[vps.length - 1].p, target)) {
        vps.push({ p: target, kind: 'target', createdByLockIndex: null });
    }
    const { points, segmentsMeta } = expandVirtualPointsToRoute(vps, [], sourceSide, targetSide);
    return { points, segmentsMeta, connectionStart, connectionEnd };
}
export function buildEditableConnection(opts) {
    const { source, target, sourceSide, targetSide, locks, anchorOffset = 10, sameSideDistance } = opts;
    const connectionStart = sourceSide ? add(source, dirBySide[sourceSide], anchorOffset) : source;
    const connectionEnd = targetSide ? add(target, dirBySide[targetSide], anchorOffset) : target;
    const vps = [
        { p: source, kind: 'source', createdByLockIndex: null },
        { p: connectionStart, kind: 'connectionStart', createdByLockIndex: null }
    ];
    if (locks.length === 0) {
        const mids = editableNoLocksMids(connectionStart, connectionEnd, sourceSide, targetSide, sameSideDistance, source, target);
        for (const m of mids)
            vps.push({ p: m, kind: "lock", createdByLockIndex: null });
    }
    else {
        for (let i = 0; i < locks.length; i++) {
            const last = vps[vps.length - 1].p;
            const next = applyLock(last, locks[i]);
            // Keep locks even if they temporarily become no-ops; this stabilizes routing during drag.
            vps.push({ p: next, kind: 'lock', createdByLockIndex: i });
        }
    }
    if (!samePoint(vps[vps.length - 1].p, connectionEnd)) {
        vps.push({ p: connectionEnd, kind: 'connectionEnd', createdByLockIndex: null });
    }
    if (!samePoint(vps[vps.length - 1].p, target)) {
        vps.push({ p: target, kind: 'target', createdByLockIndex: null });
    }
    const { points, segmentsMeta } = expandVirtualPointsToRoute(vps, locks, sourceSide, targetSide);
    return { points, segmentsMeta, connectionStart, connectionEnd };
}
export function buildConnection(opts, editingEnabled = true) {
    if (!editingEnabled) {
        const { locks, ...legacyOpts } = opts;
        return buildLegacyConnection(legacyOpts);
    }
    return buildEditableConnection(opts);
}
function dirSign(n) {
    return n === 0 ? 0 : n > 0 ? 1 : -1;
}
/**
 * Render abbreviation for orthogonal polyline:
 * 1) removes consecutive duplicates
 * 2) removes collinear points
 * 3) removes 'backtrack' (going and returning on the same axis) – removes visual 'protruding' parts
 *
 * Preserves the first and last point.
 */
export function simplifyOrthogonalPoints(points) {
    if (!points || points.length <= 2)
        return points ? points.slice() : [];
    // 1) Dedup consecutive
    const dedup = [];
    for (const p of points) {
        const last = dedup[dedup.length - 1];
        if (!last || last.x !== p.x || last.y !== p.y) {
            dedup.push(new Point(p.x, p.y));
        }
    }
    if (dedup.length <= 2)
        return dedup;
    // 2) Remove collinear middle points
    const col = [];
    col.push(dedup[0]);
    for (let i = 1; i < dedup.length - 1; i++) {
        const a = col[col.length - 1];
        const b = dedup[i];
        const c = dedup[i + 1];
        const collinear = (a.x === b.x && b.x === c.x) || // vertical
            (a.y === b.y && b.y === c.y); // horizontal
        if (!collinear)
            col.push(b);
    }
    col.push(dedup[dedup.length - 1]);
    if (col.length <= 2)
        return col;
    // 3) Remove backtracks (ABA on same axis, direction reverses)
    // If we have A->B and B->C on the same axis and the direction reverses, B is the 'protruding' point.
    const out = [col[0]];
    for (let i = 1; i < col.length; i++) {
        out.push(col[i]);
        // try to reduce while there is something
        while (out.length >= 3) {
            const A = out[out.length - 3];
            const B = out[out.length - 2];
            const C = out[out.length - 1];
            // horizontal backtrack: A->B and B->C are horizontal and change direction
            if (A.y === B.y && B.y === C.y) {
                const s1 = dirSign(B.x - A.x);
                const s2 = dirSign(C.x - B.x);
                if (s1 !== 0 && s2 !== 0 && s1 !== s2) {
                    // remove B (protrudes)
                    out.splice(out.length - 2, 1);
                    continue;
                }
            }
            // vertical backtrack
            if (A.x === B.x && B.x === C.x) {
                const s1 = dirSign(B.y - A.y);
                const s2 = dirSign(C.y - B.y);
                if (s1 !== 0 && s2 !== 0 && s1 !== s2) {
                    out.splice(out.length - 2, 1);
                    continue;
                }
            }
            break;
        }
    }
    // final dedup (just in case)
    const final = [];
    for (const p of out) {
        const last = final[final.length - 1];
        if (!last || !samePoint(last, p))
            final.push(p);
    }
    // guarantee for first/last
    if (final.length >= 2)
        return final;
    return [col[0], col[col.length - 1]];
}
function editableNoLocksMids(start, end, sourceSide, targetSide, sameSideDistance = 30, source, target) {
    // Handle null sides for orphan connections
    if (!sourceSide || !targetSide) {
        // Simple direct path for connections without connectors
        return [];
    }
    const sAxis = axisFromSide(sourceSide); // "H" for Left/Right, "V" for Top/Bottom
    const tAxis = axisFromSide(targetSide);
    // Same-axis endpoints
    if (sAxis === tAxis) {
        if (sourceSide === targetSide) {
            const src = source || start;
            const tgt = target || end;
            if (sAxis === 'H') {
                const x = sourceSide === 'Left'
                    ? Math.min(src.x, tgt.x) - sameSideDistance
                    : Math.max(src.x, tgt.x) + sameSideDistance;
                return [new Point(x, start.y), new Point(x, end.y)];
            }
            else {
                const y = sourceSide === 'Top'
                    ? Math.min(src.y, tgt.y) - sameSideDistance
                    : Math.max(src.y, tgt.y) + sameSideDistance;
                return [new Point(start.x, y), new Point(end.x, y)];
            }
        }
        if (sAxis === 'H') {
            const midX = (start.x + end.x) / 2;
            return [new Point(midX, start.y), new Point(midX, end.y)];
        }
        else {
            const midY = (start.y + end.y) / 2;
            return [new Point(start.x, midY), new Point(end.x, midY)];
        }
    }
    const aligned = start.x === end.x || start.y === end.y;
    if (aligned)
        return [];
    // Different axes -> single bend
    return sAxis === 'H' ? [new Point(end.x, start.y)] : [new Point(start.x, end.y)];
}
/**
 * Picks a stable "preview" target side during drag-to-create.
 * Use the returned side as targetSide while the target is still a floating mouse point.
 */
export function resolvePreviewTargetSide(source, target, state, opts = {}) {
    var _a;
    const h = (_a = opts.hysteresis) !== null && _a !== void 0 ? _a : 8;
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    const nextAxis = state.lastSide == null
        ? ax >= ay
            ? 'H'
            : 'V'
        : state.lastSide === 'Left' || state.lastSide === 'Right'
            ? ay > ax + h
                ? 'V'
                : 'H'
            : ax > ay + h
                ? 'H'
                : 'V';
    const side = nextAxis === 'H'
        ? dx >= 0
            ? 'Left'
            : 'Right' // target "faces" the source
        : dy >= 0
            ? 'Top'
            : 'Bottom'; // target "faces" the source
    state.lastSide = side;
    return side;
}
/**
 * Creates build options for a live preview connection while dragging.
 * - sourceSide is real (known)
 * - targetSide is derived (preview) until you drop on a real connector
 */
export function buildPreviewOptions(base, previewTargetSide) {
    return { ...base, targetSide: previewTargetSide };
}
